import { cn } from '@/lib/utils/cn';
import type { ImportOrderStatus } from '../types/import-tracking.types';

// ── Config ──────────────────────────────────────────────────────

const STATUS_INDEX: Record<ImportOrderStatus, number> = {
  QUOTE: -1,
  DRAFT: 0,
  CONFIRMED: 1,
  SHIPPED: 2,
  IN_TRANSIT: 3,
  CUSTOMS: 4,
  CUSTOMS_HOLD: 4,
  DELIVERED: 5,
  CANCELLED: -1,
};

const STEPS = [
  { label: 'Conf.', index: 1, color: '#3b82f6', gradEnd: '#6366f1' },
  { label: 'Emb.', index: 2, color: '#8b5cf6', gradEnd: '#a855f7' },
  { label: 'Trán.', index: 3, color: '#f59e0b', gradEnd: '#f97316' },
  { label: 'Adu.', index: 4, color: '#f97316', gradEnd: '#ef4444' },
  { label: 'Entr.', index: 5, color: '#10b981', gradEnd: '#059669' },
];

const STATUS_LABELS: Record<ImportOrderStatus, string> = {
  QUOTE: 'Cotización',
  DRAFT: 'Borrador',
  CONFIRMED: 'Confirmada',
  SHIPPED: 'Embarcada',
  IN_TRANSIT: 'En tránsito',
  CUSTOMS: 'En aduana',
  CUSTOMS_HOLD: 'Retenida',
  DELIVERED: 'Entregada',
  CANCELLED: 'Cancelada',
};

const STATUS_EMOJIS: Record<ImportOrderStatus, string> = {
  QUOTE: '📋',
  DRAFT: '📝',
  CONFIRMED: '✅',
  SHIPPED: '📦',
  IN_TRANSIT: '🚢',
  CUSTOMS: '🏛️',
  CUSTOMS_HOLD: '⚠️',
  DELIVERED: '🎉',
  CANCELLED: '❌',
};

// ── Component ───────────────────────────────────────────────────

interface StatusMiniProgressProps {
  status: ImportOrderStatus;
  showEta?: boolean;
  etaDate?: string | null;
  className?: string;
}

export function StatusMiniProgress({
  status,
  showEta,
  etaDate,
  className,
}: StatusMiniProgressProps) {
  if (status === 'CANCELLED') {
    return (
      <div className={cn('flex flex-col items-center gap-1', className)}>
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-bloqueo-suave border border-bloqueo-borde">
          <span className="text-xs">❌</span>
          <span className="text-[10px] font-bold text-bloqueo">Cancelada</span>
        </div>
      </div>
    );
  }

  const currentIndex = STATUS_INDEX[status];
  const isCustomsHold = status === 'CUSTOMS_HOLD';
  const isDelivered = status === 'DELIVERED';

  // Calculate ETA info
  let etaDays: number | null = null;
  if (showEta && etaDate) {
    etaDays = Math.round((new Date(etaDate).getTime() - Date.now()) / 86400000);
  }

  // SVG dimensions
  const width = 160;
  const height = 22;
  const pad = 14;
  const usableW = width - pad * 2;
  const dotR = 4;
  const currentR = 6;
  const spacing = usableW / (STEPS.length - 1);
  const lineY = height / 2;

  return (
    <div className={cn('flex flex-col items-center gap-1 w-[160px]', className)}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
        <defs>
          {STEPS.map(
            (step, i) =>
              i < STEPS.length - 1 && (
                <linearGradient
                  key={`mg-${i}`}
                  id={`mini-grad-${i}`}
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor={step.color} />
                  <stop offset="100%" stopColor={STEPS[i + 1].color} />
                </linearGradient>
              )
          )}
        </defs>

        {/* Connection lines */}
        {STEPS.map((step, i) => {
          if (i === STEPS.length - 1) return null;
          const x1 = pad + i * spacing;
          const x2 = pad + (i + 1) * spacing;
          const isCompleted = currentIndex >= STEPS[i + 1].index;
          const isCurrent = currentIndex >= step.index && currentIndex < STEPS[i + 1].index;

          return (
            <g key={`line-${i}`}>
              {/* Track */}
              <line
                x1={x1}
                y1={lineY}
                x2={x2}
                y2={lineY}
                stroke="#e2e8f0"
                strokeWidth={2.5}
                strokeLinecap="round"
              />

              {/* Completed: gradient line */}
              {isCompleted && (
                <line
                  x1={x1}
                  y1={lineY}
                  x2={x2}
                  y2={lineY}
                  stroke={`url(#mini-grad-${i})`}
                  strokeWidth={3}
                  strokeLinecap="round"
                />
              )}

              {/* Active: animated dash */}
              {isCurrent && (
                <line
                  x1={x1}
                  y1={lineY}
                  x2={x2}
                  y2={lineY}
                  stroke={step.color}
                  strokeWidth={2.5}
                  strokeDasharray="5 3"
                  strokeLinecap="round"
                  opacity={0.7}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from="0"
                    to="-16"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </line>
              )}

              {/* Future: dashed */}
              {!isCompleted && !isCurrent && (
                <line
                  x1={x1}
                  y1={lineY}
                  x2={x2}
                  y2={lineY}
                  stroke="#cbd5e1"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  strokeLinecap="round"
                  opacity={0.5}
                />
              )}
            </g>
          );
        })}

        {/* Dots */}
        {STEPS.map((step, i) => {
          const cx = pad + i * spacing;
          const cy = lineY;
          const isCompleted = currentIndex > step.index;
          const isCurrent = currentIndex === step.index;
          const showWarning = isCustomsHold && step.index === 4;

          if (showWarning) {
            return (
              <g key={`dot-${i}`}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={currentR}
                  fill="#fef2f2"
                  stroke="#ef4444"
                  strokeWidth={2}
                >
                  <animate attributeName="r" values="5;7;5" dur="1s" repeatCount="indefinite" />
                </circle>
                <text
                  x={cx}
                  y={cy + 0.5}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="8"
                >
                  ⚠️
                </text>
              </g>
            );
          }

          if (isCurrent) {
            return (
              <g key={`dot-${i}`}>
                {/* Glow */}
                <circle cx={cx} cy={cy} r={currentR + 4} fill={step.color} opacity={0.15}>
                  <animate
                    attributeName="r"
                    values={`${currentR + 3};${currentR + 7};${currentR + 3}`}
                    dur="2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.2;0.05;0.2"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
                {/* Dot */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={currentR}
                  fill={step.color}
                  stroke="white"
                  strokeWidth={2}
                />
              </g>
            );
          }

          if (isCompleted) {
            return (
              <g key={`dot-${i}`}>
                <circle cx={cx} cy={cy} r={dotR} fill={step.color} />
                <text
                  x={cx}
                  y={cy + 0.5}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="white"
                  fontSize="5"
                  fontWeight="bold"
                >
                  ✓
                </text>
              </g>
            );
          }

          return (
            <circle
              key={`dot-${i}`}
              cx={cx}
              cy={cy}
              r={dotR - 1}
              fill="white"
              stroke="#cbd5e1"
              strokeWidth={1.5}
            />
          );
        })}
      </svg>

      {/* Label row */}
      <div className="flex items-center gap-1">
        <span className="text-xs">{STATUS_EMOJIS[status]}</span>
        <span
          className="text-[10px] font-bold"
          style={{
            color:
              STEPS[Math.min(currentIndex - 1, STEPS.length - 1)]?.color ||
              (isDelivered ? '#10b981' : '#94a3b8'),
          }}
        >
          {STATUS_LABELS[status]}
        </span>
        {/* Mini ETA pill */}
        {showEta && etaDays !== null && !isDelivered && (
          <span
            className={cn(
              'text-[9px] font-bold px-1.5 py-0 rounded-full',
              etaDays <= 2
                ? 'bg-bloqueo-suave text-bloqueo'
                : etaDays <= 7
                  ? 'bg-alerta-suave text-alerta'
                  : 'bg-info-suave text-primary'
            )}
          >
            {etaDays === 0 ? 'HOY' : etaDays < 0 ? `+${Math.abs(etaDays)}d` : `${etaDays}d`}
          </span>
        )}
      </div>
    </div>
  );
}
